/**
 * Phase 14.2 — 🪙 Track Status Chips (micro-badge redesign)
 * ---------------------------------------------------------
 * Replaces the old large overlay banners (Founder Showcase, Spotlight
 * Boost Active hero card, Global Feature, Surge Active, Breakout
 * milestone) with a single horizontal row of compact premium pill
 * chips. Each chip:
 *   • is ~22-26px tall and never overlaps cover art
 *   • shows just an icon + one short label
 *   • opens a click/tap popover with rich detail (progress, reach,
 *     impressions, days left, share link, how-to-earn)
 *   • has hover-card on desktop for instant feedback
 *
 * Chips are rendered in this priority order:
 *   Spotlight Winner → Spotlight Surge → Feature Boost
 *      → Surge Active → Founder Showcase → Milestone (Breakout/etc.)
 *
 * All visual + tooltip logic lives here so callers just drop
 * `<TrackStatusChips submission={s} />` below the track title.
 */
import { useState } from "react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { getMilestone } from "@/components/MilestoneBadge";

/* ───────────────────────── helpers ────────────────────────── */
const fmt = (n) => {
  const v = Number(n || 0);
  if (v >= 1_000_000) {
    const m = v / 1_000_000;
    return m < 10 ? `${m.toFixed(1)}M` : `${Math.floor(m)}M`;
  }
  if (v >= 1000) {
    const k = v / 1000;
    return k < 10 ? `${k.toFixed(1)}K` : `${Math.floor(k)}K`;
  }
  return String(v);
};

const featureRemainingText = (s) => {
  if (!s?.feature_active) return null;
  const secs = Number(s?.feature_remaining_seconds || 0);
  if (secs <= 0) return null;
  const days = Math.floor(secs / 86400);
  const hours = Math.floor((secs % 86400) / 3600);
  if (days >= 1) return `${days}d ${hours}h left`;
  const mins = Math.floor((secs % 3600) / 60);
  return hours >= 1 ? `${hours}h ${mins}m left` : `${mins}m left`;
};

const reachFor = (s) =>
  Number(s?.boost_applied || 0) + Number(s?.floor_applied || 0);
const impressionsFor = (s) => Math.round(reachFor(s) * 8);

/* ───────────────────────── chip primitive ─────────────────── */
const Chip = ({
  children,
  testId,
  tone = "amber",
  glow = false,
  popoverContent,
}) => {
  const [open, setOpen] = useState(false);

  const palette = {
    amber: {
      bg: "from-amber-500/20 via-pink-500/15 to-amber-500/20",
      border: "border-amber-400/45",
      text: "text-amber-100",
      hover: "hover:border-amber-300/80",
      ring: "shadow-[0_0_14px_rgba(251,191,36,0.30)]",
    },
    purple: {
      bg: "from-purple-500/25 via-amber-400/18 to-pink-500/25",
      border: "border-purple-400/55",
      text: "text-purple-100",
      hover: "hover:border-purple-300/85",
      ring: "shadow-[0_0_14px_rgba(168,85,247,0.32)]",
    },
    yellow: {
      bg: "from-yellow-400/85 via-amber-400/85 to-yellow-500/85",
      border: "border-yellow-300/70",
      text: "text-black",
      hover: "hover:border-yellow-200",
      ring: "shadow-[0_0_14px_rgba(250,204,21,0.45)]",
    },
    orange: {
      bg: "from-orange-500/22 via-rose-500/15 to-orange-500/22",
      border: "border-orange-400/55",
      text: "text-orange-100",
      hover: "hover:border-orange-300/85",
      ring: "shadow-[0_0_14px_rgba(249,115,22,0.32)]",
    },
    emerald: {
      bg: "from-emerald-500/20 via-cyan-500/15 to-emerald-500/20",
      border: "border-emerald-400/55",
      text: "text-emerald-100",
      hover: "hover:border-emerald-300/85",
      ring: "shadow-[0_0_14px_rgba(16,185,129,0.32)]",
    },
  }[tone] || {};

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid={testId}
          className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-gradient-to-r ${palette.bg} border ${palette.border} ${palette.text} ${palette.hover} ${glow ? palette.ring : ""} text-[10px] font-black uppercase tracking-wider transition-all hover:-translate-y-0.5 cursor-pointer focus:outline-none focus:ring-2 focus:ring-amber-400/40`}
        >
          {children}
        </button>
      </PopoverTrigger>
      <PopoverContent
        side="top"
        align="start"
        className="w-72 bg-gradient-to-br from-[#160b22] via-[#0d0717] to-[#0a0514] border-amber-400/30 text-white p-4 shadow-2xl shadow-amber-500/10"
        data-testid={`${testId}-popover`}
      >
        {popoverContent}
      </PopoverContent>
    </Popover>
  );
};

/* ─────────────── individual chip popovers ─────────────────── */
const FeatureBoostPopover = ({ submission, isSpotlight }) => {
  const reach = reachFor(submission);
  const impressions = impressionsFor(submission);
  const pct = Math.max(
    0,
    Math.min(100, Number(submission?.feature_progress_pct || 0)),
  );
  const remaining = featureRemainingText(submission);
  const active = submission?.feature_active;
  const label = isSpotlight ? "Spotlight Surge Active" : "Feature Boost Active";
  const sub = isSpotlight
    ? "Hand-picked weekly push — amplified rotation + curated discovery slots."
    : "Currently being promoted by RGM Studio — extra placements + boosted reach.";
  const accent = isSpotlight ? "text-purple-200" : "text-amber-200";
  const barFrom = isSpotlight ? "from-purple-400" : "from-amber-400";
  const barTo = isSpotlight ? "to-amber-300" : "to-pink-400";

  return (
    <div className="space-y-3">
      <div>
        <p className={`text-[10px] uppercase tracking-[0.2em] font-black ${accent}`}>
          {isSpotlight ? "🌟 Spotlight" : "✨ Feature"}
        </p>
        <p className="text-white text-sm font-bold mt-0.5">{label}</p>
        <p className="text-white/70 text-xs mt-1 leading-snug">{sub}</p>
      </div>

      {active && (
        <div data-testid="status-chip-surge-progress">
          <div className="flex items-center justify-between mb-1">
            <span className="text-[10px] uppercase tracking-wider text-white/60 font-bold">
              Campaign Progress
            </span>
            <span className={`text-[10px] font-black ${accent}`}>
              {pct}%{remaining ? ` · ${remaining}` : ""}
            </span>
          </div>
          <div className="w-full h-1.5 bg-black/50 rounded-full overflow-hidden border border-white/10">
            <div
              className={`h-full bg-gradient-to-r ${barFrom} ${barTo} transition-[width] duration-700`}
              style={{ width: `${pct}%` }}
            />
          </div>
        </div>
      )}

      {reach > 0 && (
        <div className="grid grid-cols-2 gap-2">
          <div className="rounded-lg bg-black/40 border border-amber-400/25 px-3 py-2">
            <p className="text-[9px] uppercase tracking-wider text-amber-200/70 font-bold">
              Reach
            </p>
            <p className="text-white font-black text-sm mt-0.5">{fmt(reach)}</p>
          </div>
          <div className="rounded-lg bg-black/40 border border-pink-400/25 px-3 py-2">
            <p className="text-[9px] uppercase tracking-wider text-pink-200/70 font-bold">
              Impressions
            </p>
            <p className="text-white font-black text-sm mt-0.5">
              {fmt(impressions)}
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

const SpotlightWinnerPopover = ({ position }) => {
  const medal =
    position === 1 ? "🥇" : position === 2 ? "🥈" : position === 3 ? "🥉" : "🏆";
  return (
    <div className="space-y-2">
      <div className="flex items-center gap-3">
        <span className="text-3xl">{medal}</span>
        <div>
          <p className="text-[10px] uppercase tracking-[0.2em] font-black text-yellow-300">
            Spotlight Winner
          </p>
          <p className="text-white text-base font-black">#{position} this week</p>
        </div>
      </div>
      <p className="text-white/75 text-xs leading-snug">
        Selected by the RGM Studio A&amp;R team for the Weekly Spotlight Top 3 —
        a curated showcase of standout new music.
      </p>
    </div>
  );
};

const SurgePopover = ({ featuredAt }) => {
  const hoursAgo = featuredAt
    ? Math.max(0, Math.floor((Date.now() - new Date(featuredAt).getTime()) / 3_600_000))
    : null;
  return (
    <div className="space-y-2">
      <p className="text-[10px] uppercase tracking-[0.2em] font-black text-orange-300">
        🔥 Surge Active
      </p>
      <p className="text-white text-sm font-bold">Featured Push is live right now</p>
      <p className="text-white/75 text-xs leading-snug">
        This track is in active rotation across the discovery feed. Extra
        placements every few minutes for the next 24h.
      </p>
      {hoursAgo !== null && (
        <p className="text-orange-200/80 text-[11px] font-semibold">
          Started {hoursAgo === 0 ? "<1h" : `${hoursAgo}h`} ago
        </p>
      )}
    </div>
  );
};

const FounderShowcasePopover = () => (
  <div className="space-y-2">
    <p className="text-[10px] uppercase tracking-[0.2em] font-black text-yellow-300">
      👑 Founder Showcase
    </p>
    <p className="text-white text-sm font-bold">Hand-picked by the RGM team</p>
    <p className="text-white/75 text-xs leading-snug">
      Tracks personally selected by the RGM Studio founding team and pinned to
      the platform's premium showcase shelf.
    </p>
  </div>
);

const MilestonePopover = ({ milestone, playCount }) => (
  <div className="space-y-2">
    <div className="flex items-center gap-3">
      <span className="text-3xl">{milestone.emoji}</span>
      <div>
        <p className="text-[10px] uppercase tracking-[0.2em] font-black text-white/60">
          Milestone Unlocked
        </p>
        <p className="text-white text-base font-black">{milestone.name}</p>
      </div>
    </div>
    <p className="text-white/75 text-xs leading-snug">
      Crossed{" "}
      <span className="font-black text-white">
        {milestone.threshold.toLocaleString()}
      </span>{" "}
      plays — currently at{" "}
      <span className="font-black text-white">
        {Number(playCount || 0).toLocaleString()}
      </span>
      .
    </p>
  </div>
);

const GlobalFeaturePopover = () => (
  <div className="space-y-2">
    <p className="text-[10px] uppercase tracking-[0.2em] font-black text-purple-300">
      🌍 Global Feature
    </p>
    <p className="text-white text-sm font-bold">Featured on RGM Studio</p>
    <p className="text-white/75 text-xs leading-snug">
      This track is part of the editorial Featured rotation on the home and
      discovery feeds.
    </p>
  </div>
);

/* ────────────────────────── main row ──────────────────────── */
const TrackStatusChips = ({ submission, className = "" }) => {
  if (!submission) return null;

  const eng = submission.engagement || {};
  const isSpotlight = submission.status === "spotlight";
  const isFeatured = submission.status === "featured";
  const showFeatureBoost = isSpotlight || isFeatured;
  const showSpotlightWinner = !!eng.spotlight_position;
  // Phase 15.0 — Featured + Spotlight + Surge stack ADDITIVELY now.
  // Previously surge / global-feature were suppressed when a winner
  // chip was present which felt like Spotlight "replacing" Featured.
  // We still suppress Global Feature when a stronger Spotlight or
  // Feature Boost chip is already on screen (avoids redundancy), but
  // never because of the winner medal.
  const showSurge = !!eng.surge_active;
  const showGlobalFeature = !!eng.is_featured && !showFeatureBoost;
  const showFounder = !!submission.is_founder_showcase;
  const milestone = getMilestone(
    submission.display_plays ?? submission.play_count ?? 0,
  );

  const chips = [];

  if (showSpotlightWinner) {
    const pos = eng.spotlight_position;
    const medal =
      pos === 1 ? "🥇" : pos === 2 ? "🥈" : pos === 3 ? "🥉" : "🏆";
    chips.push(
      <Chip
        key="spotlight-winner"
        testId="status-chip-spotlight-winner"
        tone="yellow"
        glow
        popoverContent={<SpotlightWinnerPopover position={pos} />}
      >
        <span className="text-[12px] leading-none">{medal}</span>
        <span>Spotlight #{pos}</span>
      </Chip>,
    );
  }

  if (showFeatureBoost) {
    chips.push(
      <Chip
        key="feature-boost"
        testId={isSpotlight ? "status-chip-spotlight-surge" : "status-chip-feature-boost"}
        tone={isSpotlight ? "purple" : "amber"}
        glow
        popoverContent={
          <FeatureBoostPopover submission={submission} isSpotlight={isSpotlight} />
        }
      >
        <span className="text-[12px] leading-none">{isSpotlight ? "🌟" : "✨"}</span>
        <span>{isSpotlight ? "Spotlight" : "Feature Boost"}</span>
        {submission.feature_active && (
          <span className="text-white/85 normal-case font-bold tracking-normal">
            · {Math.max(0, Math.min(100, Number(submission.feature_progress_pct || 0)))}%
          </span>
        )}
      </Chip>,
    );
  }

  if (showGlobalFeature) {
    chips.push(
      <Chip
        key="global-feature"
        testId="status-chip-global-feature"
        tone="purple"
        popoverContent={<GlobalFeaturePopover />}
      >
        <span className="text-[12px] leading-none">🌍</span>
        <span>Featured</span>
      </Chip>,
    );
  }

  if (showSurge) {
    chips.push(
      <Chip
        key="surge"
        testId="status-chip-surge"
        tone="orange"
        glow
        popoverContent={<SurgePopover featuredAt={submission.featured_at} />}
      >
        <span className="text-[12px] leading-none animate-pulse">🔥</span>
        <span>Surge</span>
      </Chip>,
    );
  }

  if (showFounder) {
    chips.push(
      <Chip
        key="founder"
        testId="status-chip-founder-showcase"
        tone="yellow"
        popoverContent={<FounderShowcasePopover />}
      >
        <span className="text-[12px] leading-none">👑</span>
        <span>Founder Pick</span>
      </Chip>,
    );
  }

  if (milestone) {
    chips.push(
      <Chip
        key="milestone"
        testId="status-chip-milestone"
        tone="emerald"
        popoverContent={
          <MilestonePopover
            milestone={milestone}
            playCount={submission.display_plays ?? submission.play_count ?? 0}
          />
        }
      >
        <span className="text-[12px] leading-none">{milestone.emoji}</span>
        <span>{milestone.name}</span>
      </Chip>,
    );
  }

  if (chips.length === 0) return null;

  return (
    <div
      data-testid="track-status-chips"
      className={`flex flex-wrap items-center gap-1.5 ${className}`}
    >
      {chips}
    </div>
  );
};

export default TrackStatusChips;
